import { gsap } from 'gsap';

export default class NeonGlitch {
  constructor(element, options = {}) {
    this.element = element;

    this.options = {
      radius: 140, // rayon de l'effet autour du curseur (px)
      idleFlicker: true, // scintillement + glitch aléatoire au repos
      minDelay: 2, // délai min entre deux glitchs (s)
      maxDelay: 6, // délai max (s)
      ...options,
    };

    this.state = { x: 0, y: 0, i: 0 };

    this.onEnter = this.onEnter.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onLeave = this.onLeave.bind(this);
    this.render = this.render.bind(this);
    this.idle = this.idle.bind(this);

    // Les pseudo-éléments copient le texte via data-text
    this.element.dataset.text = this.element.textContent.trim();
    this.element.style.setProperty('--radius', `${this.options.radius}px`);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.element.addEventListener('pointerenter', this.onEnter);
    this.element.addEventListener('pointermove', this.onMove);
    this.element.addEventListener('pointerleave', this.onLeave);

    if (this.options.idleFlicker) this.scheduleIdle();
  }

  /* ---------- Curseur ---------- */

  onEnter(e) {
    if (e.pointerType !== 'mouse') return;
    const rect = this.element.getBoundingClientRect();
    // Position de départ = là où le curseur entre (pas de glissement depuis 0,0)
    this.state.x = e.clientX - rect.left;
    this.state.y = e.clientY - rect.top;

    gsap.to(this.state, {
      i: 1,
      duration: 0.35,
      ease: 'power2.out',
      overwrite: 'auto',
      onUpdate: this.render,
    });
  }

  onMove(e) {
    if (e.pointerType !== 'mouse') return;
    const rect = this.element.getBoundingClientRect();

    gsap.to(this.state, {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      duration: 0.25,
      ease: 'power3.out',
      overwrite: 'auto',
      onUpdate: this.render,
    });
  }

  onLeave() {
    gsap.to(this.state, {
      i: 0,
      duration: 0.8,
      ease: 'power2.inOut',
      overwrite: 'auto',
      onUpdate: this.render,
    });
  }

  render() {
    const s = this.element.style;
    s.setProperty('--mx', `${this.state.x}px`);
    s.setProperty('--my', `${this.state.y}px`);
    s.setProperty('--i', this.state.i.toFixed(3));
  }

  /* ---------- Animation au repos ---------- */

  scheduleIdle() {
    const { minDelay, maxDelay } = this.options;
    this.idleCall = gsap.delayedCall(
      gsap.utils.random(minDelay, maxDelay),
      this.idle,
    );
  }

  idle() {
    // Scintillement néon : l'opacité sautille rapidement
    const tl = gsap.timeline({ onComplete: () => this.scheduleIdle() });
    tl.to(this.element, { opacity: 0.75, duration: 0.04 })
      .to(this.element, { opacity: 1, duration: 0.04 })
      .to(this.element, { opacity: 0.85, duration: 0.05 })
      .to(this.element, { opacity: 1, duration: 0.06 });

    // Un glitch sur environ la moitié des scintillements
    if (Math.random() < 0.5) {
      this.element.classList.add('is-glitching');
      gsap.delayedCall(0.18, () =>
        this.element.classList.remove('is-glitching'),
      );
    }
  }

  destroy() {
    this.element.removeEventListener('pointerenter', this.onEnter);
    this.element.removeEventListener('pointermove', this.onMove);
    this.element.removeEventListener('pointerleave', this.onLeave);
    this.idleCall?.kill();
    gsap.killTweensOf([this.state, this.element]);
  }
}

document.querySelectorAll('.neon').forEach((el) => new NeonGlitch(el));
